import React from 'react';
import { getServiceAge } from '../../utils/serviceHelpers';

// ─── Types ───────────────────────────────────────────────
export type CardStatus = 'running' | 'stopped' | 'deploying' | 'error';

export interface MarketplaceService {
  id: string;
  name: string;
  status: CardStatus;
  instanceType: string;
  region: string;
  createdAgo: string;
  publicIp?: string;
  productKey: string;
  badges: string[];
}

export interface MarketplaceCategory {
  key: string;
  label: string;
}

export interface MarketplaceProduct {
  key: string;
  name: string;
  subtitle: string;
  description: string;
  categoryKey: string;
  apiCategory: string;
  createRoute: string;
  detailRoute: (id: string) => string;
  terminalRoute?: (id: string) => string;
  port: string;
  colorScheme: {
    bg: string;
    text: string;
    accent: string;
    badgeBg: string;
    badgeText: string;
    gradientFrom: string;
    gradientTo: string;
    borderColor: string;
  };
  staticBadges: string[];
  /** Ruta bajo `public`, p. ej. `/images/marketplace/rabbitmq.png` */
  logoSrc?: string;
  helpContent: {
    whatIs: string;
    whatIsDesc: string;
    useCases: string[];
    features: string[];
  };
  mapService: (svc: any) => MarketplaceService;
}

// ─── Status helpers ──────────────────────────────────────
export const mapStatus = (status: string): CardStatus => {
  const s = (status || '').toLowerCase();
  if (s === 'running') return 'running';
  if (s === 'stopped') return 'stopped';
  if (['error', 'terminated', 'deleted', 'failed'].includes(s)) return 'error';
  if (['deploying', 'initializing', 'stacking', 'creating', 'starting', 'restarting'].includes(s)) return 'deploying';
  return 'stopped';
};

const parseDate = (d: any): Date => {
  if (!d) return new Date();
  return typeof d === 'string' ? new Date(d) : d;
};

// ─── Categories ──────────────────────────────────────────
export const MARKETPLACE_CATEGORIES: MarketplaceCategory[] = [
  { key: 'ai', label: 'AI' },
  { key: 'tools', label: 'Tools' },
  { key: 'crms', label: 'CRMs' },
  { key: 'cms', label: 'CMS' },
  { key: 'ecommerce', label: 'E-Commerce' },
  { key: 'erp', label: 'ERP / Business' },
  { key: 'analytics', label: 'Analytics / BI' },
  { key: 'databases', label: 'Databases' },
];

// ─── Products ────────────────────────────────────────────
export const MARKETPLACE_PRODUCTS: MarketplaceProduct[] = [
  {
    key: 'onyx',
    name: 'Onyx',
    subtitle: 'Enterprise AI Search',
    description: 'Deploy Onyx (formerly Danswer) — open-source enterprise AI search and chat. Self-hosted ChatGPT alternative with RAG over your company data (Slack, Google Drive, Notion, GitHub, Confluence, and 40+ connectors).',
    categoryKey: 'ai',
    apiCategory: 'ONYX',
    createRoute: '/onyx/create',
    detailRoute: (id) => `/onyx/${id}`,
    terminalRoute: (id) => `/onyx/terminal/${id}`,
    port: '80 (nginx)',
    colorScheme: {
      bg: 'bg-violet-100 dark:bg-violet-900/30',
      text: 'text-violet-600 dark:text-violet-400',
      accent: 'bg-violet-600 hover:bg-violet-700',
      badgeBg: 'bg-violet-100 dark:bg-violet-900/20',
      badgeText: 'text-violet-600 dark:text-violet-300',
      gradientFrom: 'from-violet-50 dark:from-violet-900/20',
      gradientTo: 'to-purple-50 dark:to-purple-900/20',
      borderColor: 'border-violet-200 dark:border-violet-800',
    },
    staticBadges: ['RAG', 'Enterprise Search', '40+ Connectors'],
    logoSrc: '/images/marketplace/onyx.png',
    helpContent: {
      whatIs: 'What is Onyx?',
      whatIsDesc: 'Onyx (formerly Danswer) is an open-source AI platform that acts as a private ChatGPT connected to your company data. It provides enterprise search and chat with RAG (Retrieval-Augmented Generation) over 40+ data sources including Slack, Google Drive, Notion, Confluence, GitHub, Jira, and more. Works with any LLM provider (OpenAI, Anthropic, Azure, Bedrock, Ollama).',
      useCases: [
        'Private ChatGPT for your company',
        'Enterprise knowledge search',
        'Customer support AI assistant',
        'Internal documentation Q&A',
        'Code search and analysis',
        'Multi-source data retrieval',
      ],
      features: [
        'Open-source RAG platform',
        '40+ built-in data connectors',
        'Works with any LLM (OpenAI, Anthropic, Ollama, etc.)',
        'Vespa + OpenSearch hybrid search',
        'Role-based access control',
        'Slack bot integration',
        'Self-hosted on dedicated EC2',
        'Docker Compose (12 services)',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.onyxConfig || {};
      const badges: string[] = ['RAG', 'Enterprise Search'];
      if (svc.onyxImageTag) badges.push(svc.onyxImageTag);
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'Onyx',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'onyx',
        badges,
      };
    },
  },
  {
    key: 'rabbitmq',
    name: 'RabbitMQ',
    subtitle: 'Message Broker',
    description: 'Deploy managed RabbitMQ instances for event-driven architectures, microservices communication, and async processing.',
    categoryKey: 'tools',
    apiCategory: 'RABBITMQ',
    createRoute: '/rabbitmq/create',
    detailRoute: (id) => `/rabbitmq/${id}`,
    terminalRoute: (id) => `/rabbitmq/terminal/${id}`,
    port: '5672 (AMQP)',
    colorScheme: {
      bg: 'bg-orange-100 dark:bg-orange-900/30',
      text: 'text-orange-600 dark:text-orange-400',
      accent: 'bg-orange-600 hover:bg-orange-700',
      badgeBg: 'bg-orange-100 dark:bg-orange-900/20',
      badgeText: 'text-orange-600 dark:text-orange-300',
      gradientFrom: 'from-orange-50 dark:from-orange-900/20',
      gradientTo: 'to-amber-50 dark:to-amber-900/20',
      borderColor: 'border-orange-200 dark:border-orange-800',
    },
    staticBadges: ['Management UI', 'AMQP 0-9-1'],
    logoSrc: '/images/marketplace/rabbitmq.png',
    helpContent: {
      whatIs: 'What is RabbitMQ?',
      whatIsDesc: 'RabbitMQ is an open-source message broker that implements the AMQP protocol. It enables decoupled communication between microservices, task queuing, and event-driven architectures.',
      useCases: [
        'Microservices communication',
        'Background task processing',
        'Event-driven architectures',
        'Pub/Sub messaging patterns',
        'Load balancing work queues',
      ],
      features: [
        'Web Management UI (port 15672)',
        'AMQP 0-9-1 protocol support',
        'Dedicated EC2 instance',
        'Configurable storage (gp3)',
        'Public or VPC-only access',
        'Auto-provisioned security groups',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.rabbitConfig || {};
      const badges: string[] = [];
      if (cfg.enableManagementUI !== false) badges.push('Management UI');
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'RabbitMQ',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'rabbitmq',
        badges,
      };
    },
  },
  {
    key: 'n8n',
    name: 'n8n',
    subtitle: 'Workflow Automation',
    description: 'Deploy managed n8n instances for integrations, data pipelines, and process automation with 400+ connectors.',
    categoryKey: 'tools',
    apiCategory: 'N8N',
    createRoute: '/n8n/create',
    detailRoute: (id) => `/n8n/${id}`,
    port: '5678 (HTTP)',
    colorScheme: {
      bg: 'bg-rose-100 dark:bg-rose-900/30',
      text: 'text-rose-600 dark:text-rose-400',
      accent: 'bg-rose-600 hover:bg-rose-700',
      badgeBg: 'bg-rose-100 dark:bg-rose-900/20',
      badgeText: 'text-rose-600 dark:text-rose-300',
      gradientFrom: 'from-rose-50 dark:from-rose-900/20',
      gradientTo: 'to-pink-50 dark:to-pink-900/20',
      borderColor: 'border-rose-200 dark:border-rose-800',
    },
    staticBadges: ['Visual Editor', '400+ Integrations'],
    logoSrc: '/images/marketplace/n8n.png',
    helpContent: {
      whatIs: 'What is n8n?',
      whatIsDesc: 'n8n is an open-source workflow automation platform. It allows you to create automated workflows using a visual node-based editor, connecting APIs, databases, and services without writing code.',
      useCases: [
        'API integrations & data sync',
        'Automated notifications',
        'Data pipeline processing',
        'CRM & marketing automation',
        'DevOps workflow automation',
      ],
      features: [
        'Visual workflow editor',
        '400+ built-in integrations',
        'Dedicated EC2 instance',
        'Configurable storage (gp3)',
        'Webhook support',
        'Custom DNS support',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.n8nConfig || {};
      const badges: string[] = ['Workflow Editor'];
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'n8n',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'n8n',
        badges,
      };
    },
  },
  {
    key: 'strapi',
    name: 'Strapi CMS',
    subtitle: 'Headless CMS',
    description: 'Deploy Strapi v5 headless CMS instances with PostgreSQL, media storage, and a powerful admin panel.',
    categoryKey: 'cms',
    apiCategory: 'STRAPI',
    createRoute: '/strapi/create',
    detailRoute: (id) => `/strapi/${id}`,
    terminalRoute: (id) => `/strapi/terminal/${id}`,
    port: '1337 (Strapi)',
    colorScheme: {
      bg: 'bg-indigo-100 dark:bg-indigo-900/30',
      text: 'text-indigo-600 dark:text-indigo-400',
      accent: 'bg-indigo-600 hover:bg-indigo-700',
      badgeBg: 'bg-indigo-100 dark:bg-indigo-900/20',
      badgeText: 'text-indigo-600 dark:text-indigo-300',
      gradientFrom: 'from-indigo-50 dark:from-indigo-900/20',
      gradientTo: 'to-violet-50 dark:to-violet-900/20',
      borderColor: 'border-indigo-200 dark:border-indigo-800',
    },
    staticBadges: ['Admin Panel', 'PostgreSQL', 'S3 Media'],
    logoSrc: '/images/marketplace/strapi.png',
    helpContent: {
      whatIs: 'What is Strapi?',
      whatIsDesc: 'Strapi is an open-source headless CMS based on Node.js that allows you to create content APIs rapidly with a visual admin panel. It supports RESTful and GraphQL APIs out of the box.',
      useCases: [
        'Blogs and content sites',
        'Headless e-commerce',
        'Documentation portals',
        'Mobile apps with content backend',
        'Dynamic landing pages',
      ],
      features: [
        'Admin Panel (port 1337)',
        'PostgreSQL 16 database',
        'S3 Media uploads',
        'Node.js 20 + PM2',
        'Dedicated EC2 instance',
        'Custom DNS support',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.strapiConfig || {};
      const badges: string[] = ['Admin Panel', 'PostgreSQL'];
      if (cfg.enableS3Uploads !== false) badges.push('S3 Media');
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'Strapi CMS',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'strapi',
        badges,
      };
    },
  },
  {
    key: 'medusa',
    name: 'Medusa',
    subtitle: 'Headless Commerce',
    description: 'Deploy Medusa v2 headless commerce instances with product catalog, orders, payments, Redis, and a powerful admin dashboard.',
    categoryKey: 'ecommerce',
    apiCategory: 'MEDUSA',
    createRoute: '/medusa/create',
    detailRoute: (id) => `/medusa/${id}`,
    terminalRoute: (id) => `/medusa/terminal/${id}`,
    port: '9000 (HTTP)',
    colorScheme: {
      bg: 'bg-emerald-100 dark:bg-emerald-900/30',
      text: 'text-emerald-600 dark:text-emerald-400',
      accent: 'bg-emerald-600 hover:bg-emerald-700',
      badgeBg: 'bg-emerald-100 dark:bg-emerald-900/20',
      badgeText: 'text-emerald-600 dark:text-emerald-300',
      gradientFrom: 'from-emerald-50 dark:from-emerald-900/20',
      gradientTo: 'to-green-50 dark:to-green-900/20',
      borderColor: 'border-emerald-200 dark:border-emerald-800',
    },
    staticBadges: ['Admin Dashboard', 'PostgreSQL', 'Redis', 'S3 Media'],
    logoSrc: '/images/marketplace/medusa.png',
    helpContent: {
      whatIs: 'What is Medusa?',
      whatIsDesc: 'Medusa is an open-source headless commerce engine built with Node.js. It provides a complete e-commerce backend with product management, orders, payments, and a customizable admin dashboard via APIs.',
      useCases: [
        'Online stores',
        'Marketplace backends',
        'Subscription commerce',
        'B2B e-commerce',
        'Multi-region stores',
      ],
      features: [
        'Admin Dashboard (port 9000)',
        'PostgreSQL 16 database',
        'Redis for events & caching',
        'S3 Media uploads',
        'Product catalog & inventory',
        'Orders & payments',
        'Node.js 20 + PM2',
        'Dedicated EC2 instance',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.medusaConfig || {};
      const badges: string[] = ['Admin Dashboard', 'PostgreSQL', 'Redis'];
      if (cfg.enableS3Uploads !== false) badges.push('S3 Media');
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'Medusa',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'medusa',
        badges,
      };
    },
  },
  {
    key: 'odoo',
    name: 'Odoo ERP/CRM',
    subtitle: 'Business Management Suite',
    description: 'Deploy Odoo 18 Community with 30+ built-in modules: CRM, Accounting, Inventory, HR, Project Management, eCommerce, and more.',
    categoryKey: 'erp',
    apiCategory: 'ODOO',
    createRoute: '/odoo/create',
    detailRoute: (id) => `/odoo/${id}`,
    terminalRoute: (id) => `/odoo/terminal/${id}`,
    port: '8069 (HTTP)',
    colorScheme: {
      bg: 'bg-purple-100 dark:bg-purple-900/30',
      text: 'text-purple-600 dark:text-purple-400',
      accent: 'bg-purple-600 hover:bg-purple-700',
      badgeBg: 'bg-purple-100 dark:bg-purple-900/20',
      badgeText: 'text-purple-600 dark:text-purple-300',
      gradientFrom: 'from-purple-50 dark:from-purple-900/20',
      gradientTo: 'to-violet-50 dark:to-violet-900/20',
      borderColor: 'border-purple-200 dark:border-purple-800',
    },
    staticBadges: ['Admin Panel', 'PostgreSQL', '30+ Modules'],
    logoSrc: '/images/marketplace/odoo.png',
    helpContent: {
      whatIs: 'What is Odoo?',
      whatIsDesc: 'Odoo is an open-source ERP/CRM suite with 30+ integrated business applications. The Community Edition includes CRM, Sales, Accounting, Inventory, HR, Project Management, Website builder, eCommerce, and more — all in a single platform.',
      useCases: [
        'Complete business management (ERP)',
        'Customer relationship management (CRM)',
        'Accounting & invoicing',
        'Inventory & warehouse management',
        'HR & recruitment',
        'Project management',
        'eCommerce & website',
      ],
      features: [
        'Web Admin Panel (port 8069)',
        'PostgreSQL database (local)',
        '30+ built-in modules',
        'CRM, Sales & Invoicing',
        'Inventory & Warehouse',
        'HR & Recruitment',
        'Accounting & Finance',
        'Dedicated EC2 instance',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.odooConfig || {};
      const badges: string[] = ['Admin Panel', 'PostgreSQL', 'Community'];
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'Odoo ERP/CRM',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'odoo',
        badges,
      };
    },
  },
  {
    key: 'idurar',
    name: 'IDURAR',
    subtitle: 'ERP / CRM',
    description: 'Deploy IDURAR open-source ERP/CRM — invoicing, quotes, payments, and customer management on the MERN stack with MongoDB included.',
    categoryKey: 'erp',
    apiCategory: 'IDURAR',
    createRoute: '/idurar/create',
    detailRoute: (id) => `/idurar/${id}`,
    terminalRoute: (id) => `/idurar/terminal/${id}`,
    port: '3000 (HTTP)',
    colorScheme: {
      bg: 'bg-teal-100 dark:bg-teal-900/30',
      text: 'text-teal-600 dark:text-teal-400',
      accent: 'bg-teal-600 hover:bg-teal-700',
      badgeBg: 'bg-teal-100 dark:bg-teal-900/20',
      badgeText: 'text-teal-600 dark:text-teal-300',
      gradientFrom: 'from-teal-50 dark:from-teal-900/20',
      gradientTo: 'to-cyan-50 dark:to-cyan-900/20',
      borderColor: 'border-teal-200 dark:border-teal-800',
    },
    staticBadges: ['Invoicing', 'CRM', 'MongoDB'],
    logoSrc: '/images/marketplace/idurar.svg',
    helpContent: {
      whatIs: 'What is IDURAR?',
      whatIsDesc: 'IDURAR is an open-source ERP/CRM built on the MERN stack (MongoDB, Express, React, Node.js). It provides invoicing, quotes, payments, and customer management in a modern web interface — a lightweight alternative to heavyweight ERP suites.',
      useCases: [
        'Customer relationship management (CRM)',
        'Invoicing & quotes',
        'Payment tracking',
        'Client & lead management',
        'Small business back-office',
      ],
      features: [
        'Web App (port 3000)',
        'MongoDB 7 database (local, Docker)',
        'Invoicing & quote management',
        'Payments & clients',
        'MERN stack (Node.js 20 + React)',
        'Dedicated EC2 instance',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.idurarConfig || {};
      const badges: string[] = ['Invoicing', 'CRM', 'MongoDB'];
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'IDURAR',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'idurar',
        badges,
      };
    },
  },
  {
    key: 'metabase',
    name: 'Metabase BI',
    subtitle: 'BI & Dashboards',
    description: 'Open-source business intelligence and analytics. Create dashboards, charts, and data visualizations without writing SQL.',
    categoryKey: 'analytics',
    apiCategory: 'METABASE',
    createRoute: '/metabase/create',
    detailRoute: (id) => `/metabase/${id}`,
    terminalRoute: (id) => `/metabase/terminal/${id}`,
    port: '3000 (HTTP)',
    colorScheme: {
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      text: 'text-blue-600 dark:text-blue-400',
      accent: 'bg-blue-600 hover:bg-blue-700',
      badgeBg: 'bg-blue-100 dark:bg-blue-900/20',
      badgeText: 'text-blue-600 dark:text-blue-300',
      gradientFrom: 'from-blue-50 dark:from-blue-900/20',
      gradientTo: 'to-cyan-50 dark:to-cyan-900/20',
      borderColor: 'border-blue-200 dark:border-blue-800',
    },
    staticBadges: ['Dashboards', 'PostgreSQL', 'SQL Editor'],
    logoSrc: '/images/marketplace/metabase.png',
    helpContent: {
      whatIs: 'What is Metabase?',
      whatIsDesc: 'Metabase is an open-source business intelligence tool that lets you create dashboards, charts, and data visualizations. Connect to your databases and explore data without writing SQL, or use the built-in SQL editor for advanced queries.',
      useCases: [
        'Interactive dashboards & reports',
        'Data exploration & visualization',
        'SQL query editor',
        'Scheduled alerts & reports',
        'Embedded analytics (iframe)',
      ],
      features: [
        'Interactive dashboards without code',
        'Support for multiple databases',
        'Built-in SQL query editor',
        'Scheduled alerts & reports',
        'Embeddable analytics (iframe)',
        'PostgreSQL as application database',
        'HTTPS access via ALB',
        'Credentials in AWS Secrets Manager',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.metabaseConfig || {};
      const badges: string[] = ['Dashboards', 'PostgreSQL'];
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'Metabase BI',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'metabase',
        badges,
      };
    },
  },
  {
    key: 'clickhouse',
    name: 'ClickHouse',
    subtitle: 'Analytics Database',
    description: 'High-performance columnar analytics database. Process billions of rows per second with real-time OLAP queries.',
    categoryKey: 'analytics',
    apiCategory: 'CLICKHOUSE',
    createRoute: '/clickhouse/create',
    detailRoute: (id) => `/clickhouse/${id}`,
    terminalRoute: (id) => `/clickhouse/terminal/${id}`,
    port: '8123 (HTTP) / 9000 (Native)',
    colorScheme: {
      bg: 'bg-yellow-100 dark:bg-yellow-900/30',
      text: 'text-yellow-600 dark:text-yellow-400',
      accent: 'bg-yellow-600 hover:bg-yellow-700',
      badgeBg: 'bg-yellow-100 dark:bg-yellow-900/20',
      badgeText: 'text-yellow-600 dark:text-yellow-300',
      gradientFrom: 'from-yellow-50 dark:from-yellow-900/20',
      gradientTo: 'to-amber-50 dark:to-amber-900/20',
      borderColor: 'border-yellow-200 dark:border-yellow-800',
    },
    staticBadges: ['OLAP Engine', 'Columnar Storage'],
    logoSrc: '/images/marketplace/clickhouse.png',
    helpContent: {
      whatIs: 'What is ClickHouse?',
      whatIsDesc: 'ClickHouse is an open-source columnar OLAP database designed for real-time analytics. It processes billions of rows per second and is ideal for logs, analytics, time-series, and data warehousing workloads.',
      useCases: [
        'Real-time analytics dashboards',
        'Log and event analysis',
        'Time-series data processing',
        'Data warehousing & OLAP',
        'Financial and business analytics',
      ],
      features: [
        'Play UI for interactive SQL queries',
        'HTTP interface (port 8123) via ALB',
        'Native TCP (port 9000) for DBeaver, DataGrip, Metabase, Grafana',
        'Columnar storage with efficient compression',
        'SQL compatible with analytic extensions',
        'Dedicated EC2 instance',
        'HTTPS access via ALB',
        'Credentials in AWS Secrets Manager',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.clickhouseConfig || {};
      const badges: string[] = ['OLAP Engine'];
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'ClickHouse',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'clickhouse',
        badges,
      };
    },
  },
  {
    key: 'tooljet',
    name: 'ToolJet',
    subtitle: 'Low-Code Internal Tools',
    description: 'Build and deploy internal tools, dashboards, CRUD apps, and workflows with a visual drag-and-drop builder. Connect to databases, APIs, and SaaS tools.',
    categoryKey: 'tools',
    apiCategory: 'TOOLJET',
    createRoute: '/tooljet/create',
    detailRoute: (id) => `/tooljet/${id}`,
    terminalRoute: (id) => `/tooljet/terminal/${id}`,
    port: '3000 (HTTP)',
    colorScheme: {
      bg: 'bg-sky-100 dark:bg-sky-900/30',
      text: 'text-sky-600 dark:text-sky-400',
      accent: 'bg-sky-600 hover:bg-sky-700',
      badgeBg: 'bg-sky-100 dark:bg-sky-900/20',
      badgeText: 'text-sky-600 dark:text-sky-300',
      gradientFrom: 'from-sky-50 dark:from-sky-900/20',
      gradientTo: 'to-cyan-50 dark:to-cyan-900/20',
      borderColor: 'border-sky-200 dark:border-sky-800',
    },
    staticBadges: ['Drag & Drop Builder', 'PostgreSQL', 'Redis'],
    logoSrc: '/images/marketplace/tooljet.png',
    helpContent: {
      whatIs: 'What is ToolJet?',
      whatIsDesc: 'ToolJet is an open-source low-code platform for building and deploying internal tools. It provides a visual drag-and-drop app builder with 60+ UI components, 80+ data source integrations, and a built-in database (ToolJet DB).',
      useCases: [
        'Internal admin panels & dashboards',
        'CRUD apps for databases',
        'Customer support tools',
        'Approval workflows',
        'Data entry & management apps',
      ],
      features: [
        'Visual drag-and-drop app builder',
        '60+ built-in UI components',
        '80+ data source integrations',
        'Built-in no-code database (ToolJet DB)',
        'JavaScript & Python code support',
        'Multi-page apps & multiplayer editing',
        'Role-based access control',
        'PostgreSQL 16 + Redis + PostgREST included',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.tooljetConfig || {};
      const badges: string[] = ['App Builder', 'PostgreSQL', 'Redis'];
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'ToolJet',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'tooljet',
        badges,
      };
    },
  },
  {
    key: 'ghost',
    name: 'Ghost CMS',
    subtitle: 'Blog & Newsletter Platform',
    description: 'Open-source publishing platform for blogs, newsletters, and paid memberships. Includes built-in themes, email newsletters, and member subscriptions with Stripe integration.',
    categoryKey: 'cms',
    apiCategory: 'GHOST',
    createRoute: '/ghost/create',
    detailRoute: (id) => `/ghost/${id}`,
    terminalRoute: (id) => `/ghost/terminal/${id}`,
    port: '2368 (HTTP)',
    colorScheme: {
      bg: 'bg-slate-100 dark:bg-slate-900/30',
      text: 'text-slate-600 dark:text-slate-400',
      accent: 'bg-slate-600 hover:bg-slate-700',
      badgeBg: 'bg-slate-100 dark:bg-slate-900/20',
      badgeText: 'text-slate-600 dark:text-slate-300',
      gradientFrom: 'from-slate-50 dark:from-slate-900/20',
      gradientTo: 'to-gray-50 dark:to-gray-900/20',
      borderColor: 'border-slate-200 dark:border-slate-800',
    },
    staticBadges: ['Admin Panel', 'MySQL', 'Newsletters'],
    logoSrc: '/images/marketplace/ghost.png',
    helpContent: {
      whatIs: 'What is Ghost?',
      whatIsDesc: 'Ghost is an open-source publishing platform for professional blogs, newsletters, and paid memberships. Built with Node.js, it offers a modern editor, built-in SEO, email newsletters, and Stripe-powered member subscriptions.',
      useCases: [
        'Professional blogs & publications',
        'Email newsletters & campaigns',
        'Paid memberships & subscriptions',
        'Content marketing sites',
        'Documentation & knowledge bases',
      ],
      features: [
        'Admin Panel (/ghost/)',
        'MySQL 8 database',
        'Built-in email newsletters',
        'Member subscriptions with Stripe',
        'SEO optimized',
        'Built-in themes',
        'Node.js 20 runtime',
        'Dedicated EC2 instance',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.ghostConfig || {};
      const badges: string[] = ['Admin Panel', 'MySQL'];
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'Ghost CMS',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'ghost',
        badges,
      };
    },
  },
  {
    key: 'redis',
    name: 'Redis',
    subtitle: 'In-Memory Database',
    description: 'In-memory database for caching, sessions, pub/sub, and real-time data. Includes RedisInsight web admin.',
    categoryKey: 'databases',
    apiCategory: 'REDIS',
    createRoute: '/redis/create',
    detailRoute: (id) => `/redis/${id}`,
    terminalRoute: (id) => `/redis/terminal/${id}`,
    port: '6379 (Redis)',
    colorScheme: {
      bg: 'bg-red-100 dark:bg-red-900/30',
      text: 'text-red-600 dark:text-red-400',
      accent: 'bg-red-600 hover:bg-red-700',
      badgeBg: 'bg-red-100 dark:bg-red-900/20',
      badgeText: 'text-red-600 dark:text-red-300',
      gradientFrom: 'from-red-50 dark:from-red-900/20',
      gradientTo: 'to-orange-50 dark:to-orange-900/20',
      borderColor: 'border-red-200 dark:border-red-800',
    },
    staticBadges: ['RedisInsight', 'Pub/Sub', 'Cache'],
    logoSrc: '/images/marketplace/redis.png',
    helpContent: {
      whatIs: 'What is Redis?',
      whatIsDesc: 'Redis is an open-source, in-memory data structure store used as a database, cache, message broker, and streaming engine. It supports strings, hashes, lists, sets, sorted sets, and more.',
      useCases: [
        'Application caching & session storage',
        'Real-time pub/sub messaging',
        'Rate limiting & leaderboards',
        'Queue & job management',
        'Real-time analytics & counters',
      ],
      features: [
        'RedisInsight web admin UI',
        'Dedicated EC2 instance',
        'Configurable storage (gp3)',
        'Pub/Sub messaging support',
        'Persistence (RDB + AOF)',
        'Password authentication',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.redisConfig || {};
      const badges: string[] = [];
      if (cfg.enableInsight !== false) badges.push('RedisInsight');
      badges.push('Port 6379');
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'Redis',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'redis',
        badges,
      };
    },
  },
  {
    key: 'docker-host',
    name: 'Docker Host',
    subtitle: 'Bring Your Own Compose',
    description: 'Dedicated Ubuntu 24 EC2 with Docker Engine and Docker Compose pre-installed. Paste your docker-compose.yml, and Kubiy runs it automatically, exposing your chosen port through a public HTTPS endpoint. Add, edit, or remove containers from the web UI without SSH.',
    categoryKey: 'tools',
    apiCategory: 'DOCKER',
    createRoute: '/docker-host/create',
    detailRoute: (id) => `/docker-host/${id}`,
    terminalRoute: (id) => `/docker-host/terminal/${id}`,
    port: 'Custom (user-defined)',
    colorScheme: {
      bg: 'bg-sky-100 dark:bg-sky-900/30',
      text: 'text-sky-600 dark:text-sky-400',
      accent: 'bg-sky-600 hover:bg-sky-700',
      badgeBg: 'bg-sky-100 dark:bg-sky-900/20',
      badgeText: 'text-sky-600 dark:text-sky-300',
      gradientFrom: 'from-sky-50 dark:from-sky-900/20',
      gradientTo: 'to-blue-50 dark:to-blue-900/20',
      borderColor: 'border-sky-200 dark:border-sky-800',
    },
    staticBadges: ['Docker Compose', 'BYOC', 'HTTPS'],
    logoSrc: '/images/marketplace/docker.png',
    helpContent: {
      whatIs: 'What is Docker Host?',
      whatIsDesc: 'Docker Host lets you bring your own docker-compose.yml and run it on a dedicated EC2 instance managed by Kubiy. Your containers are exposed via ALB with HTTPS, and you can manage them from the web UI without SSH.',
      useCases: [
        'Run any Docker Compose stack',
        'Self-hosted SaaS applications',
        'Custom microservices deployments',
        'Development & staging environments',
        'Quick prototyping with containers',
      ],
      features: [
        'Ubuntu 24 with Docker Engine + Compose',
        'Paste your own docker-compose.yml',
        'Public HTTPS via ALB',
        'Web UI container management (add/edit/remove)',
        'Apply changes without SSH',
        'Supports Docker Hub, GHCR, ECR Public images',
        'Configurable instance type & storage',
        'Health check monitoring',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.dockerConfig || {};
      const badges: string[] = ['Docker Compose'];
      if (cfg.hostPort) badges.push(`Port ${cfg.hostPort}`);
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'Docker Host',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'docker-host',
        badges,
      };
    },
  },
  {
    key: 'frappe-education',
    name: 'Frappe Education',
    subtitle: 'Education ERP',
    description: 'Deploy Frappe Education — open-source education ERP built on ERPNext with student admissions, fee management, course scheduling, and a student portal.',
    categoryKey: 'erp',
    apiCategory: 'FRAPPE_EDUCATION',
    createRoute: '/frappe-education/create',
    detailRoute: (id) => `/frappe-education/${id}`,
    terminalRoute: (id) => `/frappe-education/terminal/${id}`,
    port: '8080 (HTTP)',
    colorScheme: {
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      text: 'text-blue-600 dark:text-blue-400',
      accent: 'bg-blue-600 hover:bg-blue-700',
      badgeBg: 'bg-blue-100 dark:bg-blue-900/20',
      badgeText: 'text-blue-600 dark:text-blue-300',
      gradientFrom: 'from-blue-50 dark:from-blue-900/20',
      gradientTo: 'to-indigo-50 dark:to-indigo-900/20',
      borderColor: 'border-blue-200 dark:border-blue-800',
    },
    staticBadges: ['Students', 'Fees', 'ERPNext'],
    logoSrc: '/images/marketplace/frappe-education.svg',
    helpContent: {
      whatIs: 'What is Frappe Education?',
      whatIsDesc: 'Frappe Education is an open-source education ERP built on top of ERPNext and the Frappe Framework. It manages the complete academic cycle — admissions, student records, fees, course scheduling, and attendance — with a built-in student portal.',
      useCases: [
        'Student admissions & enrollment',
        'Student records management',
        'Fee collection & tracking',
        'Course & class scheduling',
        'Student portal for schools & universities',
      ],
      features: [
        'Web App (port 8080)',
        'Built on ERPNext v15 (Frappe Framework)',
        'Admissions & student management',
        'Fees, schedules & attendance',
        'Student portal included',
        'Dedicated EC2 instance',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.frappeEducationConfig || {};
      const badges: string[] = ['Students', 'Fees', 'ERPNext'];
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'Frappe Education',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'frappe-education',
        badges,
      };
    },
  },
  {
    key: 'wordpress',
    name: 'WordPress',
    subtitle: 'CMS & Publishing Platform',
    description: 'Deploy managed WordPress instances with PHP 8.3, MySQL 8, Nginx, and WP-CLI pre-configured. The world\'s most popular CMS — perfect for blogs, business sites, and WooCommerce stores.',
    categoryKey: 'cms',
    apiCategory: 'WORDPRESS',
    createRoute: '/wordpress/create',
    detailRoute: (id) => `/wordpress/${id}`,
    terminalRoute: (id) => `/wordpress/terminal/${id}`,
    port: '80 (HTTP)',
    colorScheme: {
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      text: 'text-blue-600 dark:text-blue-400',
      accent: 'bg-blue-600 hover:bg-blue-700',
      badgeBg: 'bg-blue-100 dark:bg-blue-900/20',
      badgeText: 'text-blue-600 dark:text-blue-300',
      gradientFrom: 'from-blue-50 dark:from-blue-900/20',
      gradientTo: 'to-cyan-50 dark:to-cyan-900/20',
      borderColor: 'border-blue-200 dark:border-blue-800',
    },
    staticBadges: ['PHP 8.3', 'MySQL 8', 'Nginx', 'WP-CLI'],
    logoSrc: '/images/marketplace/wordpress.png',
    helpContent: {
      whatIs: 'What is WordPress?',
      whatIsDesc: 'WordPress is the world\'s most popular open-source CMS, powering over 43% of all websites. Built with PHP and MySQL, it provides a powerful admin dashboard, thousands of themes and plugins, and full extensibility — from simple blogs to complex WooCommerce e-commerce stores.',
      useCases: [
        'Personal and professional blogs',
        'Business and portfolio websites',
        'E-commerce stores with WooCommerce',
        'News portals and magazines',
        'Landing pages and marketing sites',
        'Membership and subscription sites',
      ],
      features: [
        'Admin Dashboard (/wp-admin/)',
        'PHP 8.3 + Nginx',
        'MySQL 8 database',
        'WP-CLI for command-line management',
        'Plugin and theme marketplace access',
        'WooCommerce compatible',
        'Dedicated EC2 instance',
        'Admin credentials in Secrets Manager',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.wordpressConfig || {};
      const badges: string[] = ['PHP 8.3', 'MySQL 8'];
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'WordPress',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'wordpress',
        badges,
      };
    },
  },
  {
    key: 'sqlserver',
    name: 'SQL Server Express 2022',
    subtitle: 'Relational Database',
    description: 'Deploy managed SQL Server Express 2022 on Ubuntu 22.04. Full T-SQL support, up to 10 GB per database, and compatible with SSMS, Azure Data Studio, and sqlcmd. Ideal for .NET backends and Windows-originated workloads.',
    categoryKey: 'databases',
    apiCategory: 'SQLSERVER_EXPRESS',
    createRoute: '/sqlserver/create',
    detailRoute: (id) => `/sqlserver/${id}`,
    terminalRoute: (id) => `/sqlserver/terminal/${id}`,
    port: '1433',
    colorScheme: {
      bg: 'bg-red-100 dark:bg-red-900/30',
      text: 'text-red-700 dark:text-red-400',
      accent: 'bg-red-700 hover:bg-red-800',
      badgeBg: 'bg-red-100 dark:bg-red-900/20',
      badgeText: 'text-red-700 dark:text-red-300',
      gradientFrom: 'from-red-50 dark:from-red-900/20',
      gradientTo: 'to-rose-50 dark:to-rose-900/20',
      borderColor: 'border-red-200 dark:border-red-800',
    },
    staticBadges: ['SQL Server 2022', 'Express', 'Puerto 1433', 'Ubuntu 22.04'],
    logoSrc: '/images/marketplace/sqlserver.png',
    helpContent: {
      whatIs: 'What is SQL Server Express?',
      whatIsDesc: 'SQL Server Express 2022 is the free edition of Microsoft SQL Server, offering full T-SQL support, stored procedures, triggers, views, and indexes. With a 10 GB per-database limit, it is well-suited for small to medium applications, .NET backends, and teams migrating from on-premise Windows environments to the cloud.',
      useCases: [
        '.NET and ASP.NET application backends',
        'Migrating on-premise SQL Server workloads',
        'Development and staging environments',
        'Applications requiring T-SQL compatibility',
        'Reporting and analytics on relational data',
      ],
      features: [
        'SQL Server 2022 Express Edition',
        'Full T-SQL support',
        'Up to 10 GB per database',
        'Port 1433 (SQL Server)',
        'Ubuntu 22.04 host',
        'SA password in Secrets Manager',
        'Compatible with SSMS, Azure Data Studio, sqlcmd',
        'Dedicated EC2 instance',
      ],
    },
    mapService: (svc: any): MarketplaceService => {
      const cfg = svc.metadata?.sqlserverConfig || {};
      const badges: string[] = ['SQL Server 2022', 'Port 1433'];
      if (svc.publicIp || svc.metadata?.publicIp) badges.push(svc.publicIp || svc.metadata?.publicIp);
      return {
        id: svc.id,
        name: svc.name || cfg.name || 'SQL Server Express',
        status: mapStatus(svc.status),
        instanceType: cfg.instanceType || svc.metadata?.instanceType || '—',
        region: svc.region || cfg.region || '—',
        createdAgo: getServiceAge(parseDate(svc.createdAt)),
        publicIp: svc.publicIp || svc.metadata?.publicIp,
        productKey: 'sqlserver',
        badges,
      };
    },
  },
];

// ─── Helpers ─────────────────────────────────────────────
export const getProductByKey = (key: string): MarketplaceProduct | undefined =>
  MARKETPLACE_PRODUCTS.find((p) => p.key === key);

export const getProductsByCategory = (categoryKey: string): MarketplaceProduct[] =>
  MARKETPLACE_PRODUCTS.filter((p) => p.categoryKey === categoryKey);

export const getCategoryLabel = (key: string): string =>
  MARKETPLACE_CATEGORIES.find((c) => c.key === key)?.label || key;
